import api from './api.service';

export type LogSourceType = 'syslog' | 'snmp' | 'api' | 'agent';
export type FirewallVendor = 'cisco' | 'fortigate' | 'paloalto' | 'checkpoint' | 'generic';
export type ParserType = 'auto' | 'cisco' | 'fortigate' | 'paloalto' | 'cef' | 'leef' | 'json' | 'syslog-rfc3164' | 'syslog-rfc5424';
export type LogSeverity = 'emergency' | 'alert' | 'critical' | 'error' | 'warning' | 'notice' | 'info' | 'debug';

export interface LogSource {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  sourceType: LogSourceType;
  vendor: FirewallVendor;
  listenPort?: number;
  sourceIp?: string;
  parserType: ParserType;
  customPattern?: string;
  filterSeverity?: LogSeverity[];
  filterKeywords?: string[];
  excludeKeywords?: string[];
  tags?: string[];
  metadata?: Record<string, unknown>;
  totalLogsReceived: number;
  lastLogAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FirewallLog {
  id: string;
  sourceId: string;
  timestamp: string;
  receivedAt: string;
  sourceIp: string;
  sourceHostname?: string;
  vendor: FirewallVendor;
  facility?: string;
  severity: LogSeverity;
  priority?: number;
  rawMessage: string;
  parsedMessage?: string;
  action?: string;
  protocol?: string;
  srcIp?: string;
  srcPort?: number;
  dstIp?: string;
  dstPort?: number;
  srcZone?: string;
  dstZone?: string;
  interfaceIn?: string;
  interfaceOut?: string;
  policyId?: string;
  policyName?: string;
  sessionId?: string;
  bytesIn?: number;
  bytesOut?: number;
  packetsIn?: number;
  packetsOut?: number;
  duration?: number;
  application?: string;
  category?: string;
  user?: string;
  threatType?: string;
  threatName?: string;
  threatSeverity?: string;
  parsedFields?: Record<string, unknown>;
  tags?: string[];
}

export interface LogQueryResult {
  logs: FirewallLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface LogStats {
  sourceId: string;
  totalLogs: number;
  logsPerSecond: number;
  bytesReceived: number;
  lastLogAt?: string;
  errorCount: number;
  severityBreakdown: Record<LogSeverity, number>;
  actionBreakdown: Record<string, number>;
  topSrcIps: Array<{ ip: string; count: number }>;
  topDstIps: Array<{ ip: string; count: number }>;
}

export interface VendorInfo {
  id: string;
  name: string;
  description: string;
  parserTypes: string[];
}

export interface ParserTypeInfo {
  id: string;
  name: string;
  description: string;
}

export interface CreateLogSourceData {
  name: string;
  description?: string;
  enabled?: boolean;
  sourceType?: LogSourceType;
  vendor?: FirewallVendor;
  listenPort?: number;
  sourceIp?: string;
  parserType?: ParserType;
  customPattern?: string;
  filterSeverity?: LogSeverity[];
  filterKeywords?: string[];
  excludeKeywords?: string[];
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface LogQueryParams {
  sourceId?: string;
  startTime?: string;
  endTime?: string;
  severity?: LogSeverity[];
  action?: string[];
  srcIp?: string;
  dstIp?: string;
  srcPort?: number;
  dstPort?: number;
  protocol?: string;
  searchQuery?: string;
  limit?: number;
  offset?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

// Firewall Alert Types
export type FirewallAlertType = 'high_severity' | 'port_scan' | 'brute_force' | 'ddos' | 'blocked_spike' | 'threat_detected';
export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface FirewallAlertConfig {
  tenantId: string;
  enabled: boolean;
  alertOnEmergency: boolean;
  alertOnAlert: boolean;
  alertOnCritical: boolean;
  detectPortScanning: boolean;
  portScanThreshold: number;
  portScanWindowMinutes: number;
  detectBruteForce: boolean;
  bruteForceThreshold: number;
  bruteForceWindowMinutes: number;
  detectDDoS: boolean;
  ddosThreshold: number;
  ddosWindowMinutes: number;
  detectBlockedSpike: boolean;
  blockedSpikeThreshold: number;
  blockedSpikeWindowMinutes: number;
  alertOnThreat: boolean;
  threatSeverityThreshold: string;
  cooldownMinutes: number;
}

export interface FirewallAlert {
  id: string;
  timestamp: string;
  type: FirewallAlertType;
  severity: AlertSeverity;
  sourceIp?: string;
  targetIp?: string;
  description: string;
  logCount: number;
  sampleLogs: string[];
  metadata: Record<string, any>;
  aiAnalysis?: {
    summary: string;
    rootCause: string;
    impact: string;
    recommendations: string[];
    confidenceScore: number;
    analyzedAt?: string;
  };
}

export interface FirewallAlertDetectionResult {
  tenantId: string;
  timestamp: string;
  duration: number;
  alertsDetected: number;
  alertsCreated: number;
  alerts: FirewallAlert[];
  logsAnalyzed: number;
}

export interface FirewallAlertStatistics {
  totalAlerts: number;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
  topSourceIps: Array<{ ip: string; count: number }>;
  trend: Array<{ date: string; count: number }>;
}

export const logCollectionApi = {
  // Log Sources
  getSources: async (): Promise<{ success: boolean; data: LogSource[] }> => {
    const response = await api.get<{ success: boolean; data: LogSource[] }>('/v1/log-collection/sources');
    return response;
  },

  getSource: async (id: string): Promise<{ success: boolean; data: LogSource }> => {
    const response = await api.get<{ success: boolean; data: LogSource }>(`/v1/log-collection/sources/${id}`);
    return response;
  },

  createSource: async (data: CreateLogSourceData): Promise<{ success: boolean; data: LogSource }> => {
    const response = await api.post<{ success: boolean; data: LogSource }>('/v1/log-collection/sources', data);
    return response;
  },

  updateSource: async (id: string, data: Partial<CreateLogSourceData>): Promise<{ success: boolean; data: LogSource }> => {
    const response = await api.put<{ success: boolean; data: LogSource }>(`/v1/log-collection/sources/${id}`, data);
    return response;
  },

  deleteSource: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete<{ success: boolean; message: string }>(`/v1/log-collection/sources/${id}`);
    return response;
  },

  // Logs
  queryLogs: async (params: LogQueryParams): Promise<{ success: boolean; data: LogQueryResult }> => {
    const queryParams = new URLSearchParams();

    if (params.sourceId) queryParams.append('sourceId', params.sourceId);
    if (params.startTime) queryParams.append('startTime', params.startTime);
    if (params.endTime) queryParams.append('endTime', params.endTime);
    if (params.severity) params.severity.forEach(s => queryParams.append('severity', s));
    if (params.action) params.action.forEach(a => queryParams.append('action', a));
    if (params.srcIp) queryParams.append('srcIp', params.srcIp);
    if (params.dstIp) queryParams.append('dstIp', params.dstIp);
    if (params.srcPort) queryParams.append('srcPort', params.srcPort.toString());
    if (params.dstPort) queryParams.append('dstPort', params.dstPort.toString());
    if (params.protocol) queryParams.append('protocol', params.protocol);
    if (params.searchQuery) queryParams.append('searchQuery', params.searchQuery);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.sortField) queryParams.append('sortField', params.sortField);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get<{ success: boolean; data: LogQueryResult }>(`/v1/log-collection/logs?${queryParams.toString()}`);
    return response;
  },

  // Stats
  getStats: async (sourceId?: string): Promise<{ success: boolean; data: LogStats[] }> => {
    const url = sourceId
      ? `/v1/log-collection/stats?sourceId=${sourceId}`
      : '/v1/log-collection/stats';
    const response = await api.get<{ success: boolean; data: LogStats[] }>(url);
    return response;
  },

  // Vendors
  getVendors: async (): Promise<{ success: boolean; data: { vendors: VendorInfo[]; parserTypes: ParserTypeInfo[] } }> => {
    const response = await api.get<{ success: boolean; data: { vendors: VendorInfo[]; parserTypes: ParserTypeInfo[] } }>('/v1/log-collection/vendors');
    return response;
  },

  // Firewall Alerts
  getAlertConfig: async (): Promise<{ success: boolean; data: FirewallAlertConfig }> => {
    const response = await api.get<{ success: boolean; data: FirewallAlertConfig }>('/v1/log-collection/alerts/config');
    return response;
  },

  updateAlertConfig: async (config: Partial<FirewallAlertConfig>): Promise<{ success: boolean; data: FirewallAlertConfig }> => {
    const response = await api.put<{ success: boolean; data: FirewallAlertConfig }>('/v1/log-collection/alerts/config', config);
    return response;
  },

  runAlertDetection: async (): Promise<{ success: boolean; data: FirewallAlertDetectionResult }> => {
    const response = await api.post<{ success: boolean; data: FirewallAlertDetectionResult }>('/v1/log-collection/alerts/detect', {});
    return response;
  },

  getAlerts: async (params?: { limit?: number; type?: FirewallAlertType }): Promise<{ success: boolean; data: FirewallAlert[]; meta: { total: number; limit: number } }> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.type) queryParams.append('type', params.type);

    const url = queryParams.toString()
      ? `/v1/log-collection/alerts?${queryParams.toString()}`
      : '/v1/log-collection/alerts';
    const response = await api.get<{ success: boolean; data: FirewallAlert[]; meta: { total: number; limit: number } }>(url);
    return response;
  },

  getAlertStatistics: async (params?: { startTime?: number; endTime?: number }): Promise<{ success: boolean; data: FirewallAlertStatistics }> => {
    const queryParams = new URLSearchParams();
    if (params?.startTime) queryParams.append('startTime', params.startTime.toString());
    if (params?.endTime) queryParams.append('endTime', params.endTime.toString());

    const url = queryParams.toString()
      ? `/v1/log-collection/alerts/statistics?${queryParams.toString()}`
      : '/v1/log-collection/alerts/statistics';
    const response = await api.get<{ success: boolean; data: FirewallAlertStatistics }>(url);
    return response;
  },

  analyzeAlert: async (alertId: string): Promise<{ success: boolean; data: { alertId: string; analysis: FirewallAlert['aiAnalysis'] } }> => {
    const response = await api.post<{ success: boolean; data: { alertId: string; analysis: FirewallAlert['aiAnalysis'] } }>(`/v1/log-collection/alerts/${alertId}/analyze`, {});
    return response;
  },
};

export default logCollectionApi;
